import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Sparkles, ArrowLeft, Loader2, MailCheck, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { authApi } from '../services/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map((e) => e.msg).join(', ')
          : 'Something went wrong. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-3 mb-8">
          <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-xl flex items-center justify-center shadow-lg shadow-teal-500/25">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <span className="text-xl font-bold text-slate-800">InterviewWala</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
          {!submitted ? (
            <>
              <div className="mb-6">
                <div className="w-12 h-12 rounded-xl bg-teal-50 flex items-center justify-center mb-4">
                  <Mail className="w-6 h-6 text-teal-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-800 mb-2">Forgot your password?</h1>
                <p className="text-slate-500 text-sm leading-relaxed">
                  Enter the email associated with your account and we'll send you a link to reset your password.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoFocus
                    className="h-12 bg-white border-slate-200 focus:border-teal-500 focus:ring-teal-500"
                    data-testid="forgot-password-email-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white font-semibold shadow-lg shadow-teal-500/25 transition-all duration-200"
                  disabled={loading}
                  data-testid="forgot-password-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending reset link...
                    </>
                  ) : (
                    <>Send reset link</>
                  )}
                </Button>
              </form>
            </>
          ) : (
            <div data-testid="forgot-password-success">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center mb-4">
                <MailCheck className="w-6 h-6 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-800 mb-2">Check your inbox</h1>
              <p className="text-slate-500 text-sm leading-relaxed mb-2">
                If an account with <span className="font-semibold text-slate-700">{email}</span> exists, we've sent a password reset link to it.
              </p>
              <p className="text-slate-500 text-sm leading-relaxed mb-6">
                The link expires in <strong>1 hour</strong>. Didn't get it? Check your spam folder or try again.
              </p>
              <Button
                variant="outline"
                className="w-full h-11"
                onClick={() => {
                  setSubmitted(false);
                  setEmail('');
                }}
                data-testid="forgot-password-try-again"
              >
                Try a different email
              </Button>
            </div>
          )}

          <div className="mt-6 pt-6 border-t border-slate-200">
            <Link
              to="/login"
              className="flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-teal-600 font-medium transition-colors"
              data-testid="forgot-password-back-to-login"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
